import { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { labelBrand, escapeHtml } from "@/lib/labelBrand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Printer, QrCode, Search } from "lucide-react";

export function BatchQRDialog({ open, onOpenChange }) {
  const user = useAuth()?.user;
  const [parts, setParts] = useState([]);
  const [sel, setSel] = useState({});
  const [q, setQ] = useState("");
  const hidden = useRef(null);

  useEffect(() => {
    if (open) api.get("/parts").then((r) => { setParts(r.data); setSel({}); });
  }, [open]);

  const filtered = parts.filter((p) => !q || (p.descricao + p.codigo_interno).toLowerCase().includes(q.toLowerCase()));
  const selected = parts.filter((p) => sel[p.id]);
  const toggle = (id) => setSel((s) => ({ ...s, [id]: !s[id] }));
  const allVisible = filtered.length > 0 && filtered.every((p) => sel[p.id]);
  const toggleAll = () => { const n = { ...sel }; filtered.forEach((p) => (n[p.id] = !allVisible)); setSel(n); };

  const print = () => {
    const canvases = hidden.current?.querySelectorAll("canvas") || [];
    const map = {};
    canvases.forEach((c) => (map[c.dataset.pid] = c.toDataURL("image/png")));
    const brand = labelBrand(user);
    const cells = selected.map((p) => `
      <div class="label">
        ${brand.hasBrand ? `<div class="brand">${brand.html}</div>` : ""}
        <img class="qr" src="${map[p.id] || ""}" />
        <div class="desc">${escapeHtml(p.descricao)}</div>
        <div class="code">${escapeHtml(p.codigo_interno)}</div>
        <div class="loc">${[p.localizacao?.almoxarifado, p.localizacao?.estante, p.localizacao?.prateleira, p.localizacao?.caixa].filter(Boolean).map(escapeHtml).join(" · ")}</div>
      </div>`).join("");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Etiquetas QR Peça360</title><style>
      body{font-family:Arial,sans-serif;margin:0;padding:12px}
      .grid{display:flex;flex-wrap:wrap;gap:10px}
      .label{width:200px;border:1px solid #cbd5e1;border-radius:8px;padding:10px;text-align:center;page-break-inside:avoid}
      .brand{display:flex;align-items:center;justify-content:center;gap:6px;border-bottom:2px solid #0F4C81;padding-bottom:4px;margin-bottom:6px}
      .brand .logo{height:22px}.brand span{font-weight:800;color:#0F4C81;font-size:15px}
      .label img.qr{width:150px;height:150px}
      .desc{font-size:12px;font-weight:bold;margin-top:6px}
      .code{font-family:monospace;font-size:14px;color:#0F4C81}.loc{font-size:11px;color:#334155}
    </style></head><body><div class="grid">${cells}</div>
    <script>window.onload=()=>setTimeout(()=>window.print(),400)</script></body></html>`);
    w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><QrCode className="h-6 w-6" /> Etiquetas QR em lote</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar peças..." className="pl-9 bg-slate-950 border-slate-700" data-testid="input-batch-qr-filter" />
        </div>
        <div className="flex items-center justify-between text-sm text-slate-400 px-1">
          <button onClick={toggleAll} className="text-sky-400 hover:text-sky-300" data-testid="btn-batch-select-all">{allVisible ? "Desmarcar todos" : "Selecionar todos"}</button>
          <span>{selected.length} selecionada(s)</span>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1 pr-1">
          {filtered.map((p) => (
            <label key={p.id} data-testid={`batch-qr-item-${p.id}`} className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2 cursor-pointer hover:bg-slate-800/40">
              <Checkbox checked={!!sel[p.id]} onCheckedChange={() => toggle(p.id)} />
              <div className="min-w-0"><div className="text-slate-100 text-sm truncate">{p.descricao}</div><div className="font-mono text-xs text-sky-400">{p.codigo_interno}</div></div>
            </label>
          ))}
        </div>
        <div ref={hidden} className="absolute -left-[9999px] top-0">
          {selected.map((p) => (
            <QRCodeCanvas key={p.id} value={`${window.location.origin}/pecas/${p.id}`} size={150} level="M" data-pid={p.id} />
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Fechar</Button>
          <Button data-testid="btn-batch-print" onClick={print} disabled={selected.length === 0} className="bg-sky-700 hover:bg-sky-600"><Printer className="h-4 w-4 mr-1" /> Imprimir {selected.length} etiqueta(s)</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
